/**
 * Bloco «Ferramentas deste domínio» — D5 (30/Set/2026), prancha 00-B/2a.
 *
 * As 13 páginas que eram entradas planas do menu viram linhas aqui, no FIM da página do
 * domínio a que pertencem. Cada linha lê o `efeito` do catálogo; o clique abre a página
 * própria dentro do shell (`/admin/<dominio>/<id>`). Só aponta — nunca edita (1 caminho
 * de escrita por dado; a ferramenta é dona do próprio ciclo, spec §4).
 *
 * Gate: mesma regra do `DomainSection`/rail — cap presente ⇒ link; cap ausente +
 * `omitWhenBarred` ⇒ ausente (fail-closed, P19); cap ausente ⇒ linha locked com o tier
 * (nunca some). `ferramentasVisiveis` é a fonte única que o índice da `DomainPage` também
 * lê, para bloco e índice nunca discordarem sobre a mesma ferramenta.
 *
 * Desvio DECLARADO da prancha: ela desenha «7 ativos» / «6 ativas» à direita da linha.
 * Não há fonte carregada para essas contagens no hub, e o hub não chuta
 * (estado-resolver.ts) — entram quando houver hook por ferramenta (Item 555).
 */
import { ChevronRight, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useCapabilities } from '../../../hooks/use-capability';

import { catalogTarget, ferramentasByDominio } from './catalog';

import type { ConfigCatalogItem, DominioId } from './catalog';

export const FERRAMENTAS_ANCHOR = 'ferramentas';

export function ferramentasVisiveis(
  dominio: DominioId,
  capabilities: readonly string[],
): ConfigCatalogItem[] {
  return ferramentasByDominio(dominio).filter(
    (f) => f.omitWhenBarred !== true || capabilities.includes(f.capability),
  );
}

const ROW = 'flex items-center gap-3 rounded-lg border border-border-subtle bg-surface px-4 py-3';

export function FerramentasDoDominio({
  dominio,
}: {
  readonly dominio: DominioId;
}): JSX.Element | null {
  const capabilities = useCapabilities();
  const itens = ferramentasVisiveis(dominio, capabilities);
  if (itens.length === 0) return null;
  return (
    <section
      id={FERRAMENTAS_ANCHOR}
      aria-labelledby={`${FERRAMENTAS_ANCHOR}-titulo`}
      className="scroll-mt-24 py-8"
    >
      <header className="mb-4">
        <h2 id={`${FERRAMENTAS_ANCHOR}-titulo`} className="text-lg font-semibold text-text-primary">
          Ferramentas deste domínio
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          Abrem em página própria e salvam por conta delas.
        </p>
      </header>
      <ul className="space-y-2">
        {itens.map((f) => {
          const ok = capabilities.includes(f.capability);
          const corpo = (
            <>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-text-primary">{f.label}</span>
                <span className="block text-sm text-text-secondary">{f.efeito}</span>
              </span>
              {!ok && f.tierLabel !== undefined && (
                <span className="rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-accent">
                  {f.tierLabel}
                </span>
              )}
              {ok ? (
                <ChevronRight aria-hidden className="h-4 w-4 flex-shrink-0 text-text-secondary" />
              ) : (
                <Lock aria-hidden className="h-4 w-4 flex-shrink-0 text-text-secondary" />
              )}
            </>
          );
          return (
            <li key={f.id}>
              {ok ? (
                <Link to={catalogTarget(f)} className={`${ROW} hover:bg-surface-raised`}>
                  {corpo}
                </Link>
              ) : (
                <div
                  className={`${ROW} opacity-70`}
                  aria-disabled
                  title={`${f.label} — disponível no ${f.tierLabel ?? 'plano superior'}`}
                >
                  {corpo}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
